import type {
  AnnouncementAudience,
  AnnouncementPartyContext,
  AnnouncementPartyCount,
  AnnouncementPartyRelation,
  AnnouncementTargeting,
} from "./types";

export const ANNOUNCEMENT_AUDIENCES = [
  "all",
  "no_award_show_party",
  "single_award_show_party",
] as const satisfies readonly AnnouncementAudience[];

export const ANNOUNCEMENT_PARTY_RELATIONS = [
  "any",
  "none",
  "in_party",
  "owner",
  "member",
] as const satisfies readonly AnnouncementPartyRelation[];

export const ANNOUNCEMENT_PARTY_COUNTS = [
  "any",
  "one",
  "multiple",
] as const satisfies readonly AnnouncementPartyCount[];

export interface ShowPartyLink {
  userId: string;
  partyId: string;
  isOwner: boolean;
  isMember: boolean;
  partyName?: string | null;
  inviteCode?: string | null;
  createdAt?: string | null;
}

interface IndexedShowParty {
  isOwner: boolean;
  isMember: boolean;
  partyName: string;
  inviteCode: string;
  createdAt: string;
}

function indexShowPartyLinks(links: readonly ShowPartyLink[]): {
  partiesByUser: Map<string, Map<string, IndexedShowParty>>;
  partySizes: Map<string, number>;
} {
  const partiesByUser = new Map<string, Map<string, IndexedShowParty>>();
  const usersByParty = new Map<string, Set<string>>();

  for (const link of links) {
    let parties = partiesByUser.get(link.userId);
    if (!parties) {
      parties = new Map();
      partiesByUser.set(link.userId, parties);
    }
    const existing = parties.get(link.partyId) ?? {
      isOwner: false,
      isMember: false,
      partyName: "",
      inviteCode: "",
      createdAt: "",
    };
    parties.set(link.partyId, {
      isOwner: existing.isOwner || link.isOwner,
      isMember: existing.isMember || link.isMember,
      partyName: existing.partyName || (link.partyName?.trim() ?? ""),
      inviteCode: existing.inviteCode || (link.inviteCode?.trim() ?? ""),
      createdAt: existing.createdAt || (link.createdAt?.trim() ?? ""),
    });

    let users = usersByParty.get(link.partyId);
    if (!users) {
      users = new Set();
      usersByParty.set(link.partyId, users);
    }
    users.add(link.userId);
  }

  const partySizes = new Map<string, number>();
  for (const [partyId, users] of usersByParty) {
    partySizes.set(partyId, users.size);
  }

  return { partiesByUser, partySizes };
}

export interface ShowPartyStats {
  inPartyCount: number;
  ownerCount: number;
  memberCount: number;
  inPartySizes: number[];
  ownerSizes: number[];
  memberSizes: number[];
}

export const EMPTY_SHOW_PARTY_STATS: ShowPartyStats = {
  inPartyCount: 0,
  ownerCount: 0,
  memberCount: 0,
  inPartySizes: [],
  ownerSizes: [],
  memberSizes: [],
};

export function isAnnouncementAudience(
  value: string,
): value is AnnouncementAudience {
  return (ANNOUNCEMENT_AUDIENCES as readonly string[]).includes(value);
}

export function parseAnnouncementAudience(
  value: unknown,
): AnnouncementAudience {
  if (typeof value === "string" && isAnnouncementAudience(value)) {
    return value;
  }
  return "all";
}

export function isAnnouncementPartyRelation(
  value: string,
): value is AnnouncementPartyRelation {
  return (ANNOUNCEMENT_PARTY_RELATIONS as readonly string[]).includes(value);
}

export function parseAnnouncementPartyRelation(
  value: unknown,
): AnnouncementPartyRelation {
  if (typeof value === "string" && isAnnouncementPartyRelation(value)) {
    return value;
  }
  return "any";
}

export function isAnnouncementPartyCount(
  value: string,
): value is AnnouncementPartyCount {
  return (ANNOUNCEMENT_PARTY_COUNTS as readonly string[]).includes(value);
}

export function parseAnnouncementPartyCount(
  value: unknown,
): AnnouncementPartyCount {
  if (typeof value === "string" && isAnnouncementPartyCount(value)) {
    return value;
  }
  return "any";
}

export function parseAnnouncementPartySizeBound(
  value: unknown,
): number | null {
  if (value == null) {
    return null;
  }
  if (typeof value === "number") {
    if (!Number.isInteger(value) || value < 1) {
      return null;
    }
    return value;
  }
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === "" || !/^\d+$/.test(trimmed)) {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return null;
  }
  return parsed;
}

export function hasAnnouncementPartySizeFilter(
  min: number | null | undefined,
  max: number | null | undefined,
): boolean {
  return min != null || max != null;
}

export function targetingFromAudience(
  audience: AnnouncementAudience,
): AnnouncementTargeting {
  switch (audience) {
    case "no_award_show_party":
      return {
        partyRelation: "none",
        partyCount: "any",
        partySizeMin: null,
        partySizeMax: null,
      };
    case "single_award_show_party":
      return {
        partyRelation: "in_party",
        partyCount: "one",
        partySizeMin: null,
        partySizeMax: null,
      };
    default:
      return {
        partyRelation: "any",
        partyCount: "any",
        partySizeMin: null,
        partySizeMax: null,
      };
  }
}

export function audienceFromTargeting(
  partyRelation: AnnouncementPartyRelation,
  partyCount: AnnouncementPartyCount,
): AnnouncementAudience {
  if (partyRelation === "none") {
    return "no_award_show_party";
  }
  if (partyRelation === "in_party" && partyCount === "one") {
    return "single_award_show_party";
  }
  return "all";
}

function hasExplicitFilter(value: unknown): boolean {
  return value != null && String(value).trim().length > 0;
}

export function resolveAnnouncementTargeting(input: {
  partyRelation?: unknown;
  partyCount?: unknown;
  partySizeMin?: unknown;
  partySizeMax?: unknown;
  audience?: unknown;
}): AnnouncementTargeting {
  const partySizeMin = parseAnnouncementPartySizeBound(input.partySizeMin);
  const partySizeMax = parseAnnouncementPartySizeBound(input.partySizeMax);

  if (hasExplicitFilter(input.partyRelation)) {
    return {
      partyRelation: parseAnnouncementPartyRelation(input.partyRelation),
      partyCount: parseAnnouncementPartyCount(input.partyCount),
      partySizeMin,
      partySizeMax,
    };
  }

  const fromAudience = targetingFromAudience(
    parseAnnouncementAudience(input.audience),
  );
  return {
    partyRelation: fromAudience.partyRelation,
    partyCount: hasExplicitFilter(input.partyCount)
      ? parseAnnouncementPartyCount(input.partyCount)
      : fromAudience.partyCount,
    partySizeMin,
    partySizeMax,
  };
}

export function targetingNeedsPartyStats(
  partyRelation: AnnouncementPartyRelation,
  partySizeMin?: number | null,
  partySizeMax?: number | null,
): boolean {
  return (
    partyRelation !== "any" ||
    hasAnnouncementPartySizeFilter(partySizeMin, partySizeMax)
  );
}

export function matchesAnnouncementPartyCount(
  count: number,
  partyCount: AnnouncementPartyCount,
): boolean {
  if (count < 1) {
    return false;
  }
  if (partyCount === "one") {
    return count === 1;
  }
  if (partyCount === "multiple") {
    return count >= 2;
  }
  return true;
}

export function matchesAnnouncementPartySize(
  sizes: readonly number[],
  min: number | null | undefined,
  max: number | null | undefined,
): boolean {
  if (!hasAnnouncementPartySizeFilter(min, max)) {
    return true;
  }
  const lower = min ?? Number.NEGATIVE_INFINITY;
  const upper = max ?? Number.POSITIVE_INFINITY;
  return sizes.some((size) => size >= lower && size <= upper);
}

function sizesForRelation(
  partyRelation: AnnouncementPartyRelation,
  stats: {
    inPartySizes?: readonly number[];
    ownerSizes?: readonly number[];
    memberSizes?: readonly number[];
  },
): readonly number[] {
  if (partyRelation === "owner") {
    return stats.ownerSizes ?? [];
  }
  if (partyRelation === "member") {
    return stats.memberSizes ?? [];
  }
  return stats.inPartySizes ?? [];
}

export function userMatchesAnnouncementTargeting(input: {
  partyRelation: AnnouncementPartyRelation;
  partyCount: AnnouncementPartyCount;
  partySizeMin?: number | null;
  partySizeMax?: number | null;
  inPartyCount: number;
  ownerCount: number;
  memberCount: number;
  inPartySizes?: readonly number[];
  ownerSizes?: readonly number[];
  memberSizes?: readonly number[];
}): boolean {
  let matchesRelation = false;
  switch (input.partyRelation) {
    case "any":
      matchesRelation = true;
      break;
    case "none":
      matchesRelation = input.inPartyCount === 0;
      break;
    case "in_party":
      matchesRelation = matchesAnnouncementPartyCount(
        input.inPartyCount,
        input.partyCount,
      );
      break;
    case "owner":
      matchesRelation = matchesAnnouncementPartyCount(
        input.ownerCount,
        input.partyCount,
      );
      break;
    case "member":
      matchesRelation = matchesAnnouncementPartyCount(
        input.memberCount,
        input.partyCount,
      );
      break;
  }

  if (!matchesRelation) {
    return false;
  }
  if (input.partyRelation === "none") {
    return true;
  }
  return matchesAnnouncementPartySize(
    sizesForRelation(input.partyRelation, input),
    input.partySizeMin,
    input.partySizeMax,
  );
}

function sortSizes(sizes: number[]): number[] {
  return [...sizes].sort((left, right) => left - right);
}

export function buildShowPartyStats(
  links: readonly ShowPartyLink[],
): Map<string, ShowPartyStats> {
  const { partiesByUser, partySizes } = indexShowPartyLinks(links);
  const stats = new Map<string, ShowPartyStats>();

  for (const [userId, parties] of partiesByUser) {
    let ownerCount = 0;
    let memberCount = 0;
    const inPartySizes: number[] = [];
    const ownerSizes: number[] = [];
    const memberSizes: number[] = [];

    for (const [partyId, flags] of parties) {
      const size = partySizes.get(partyId) ?? 0;
      inPartySizes.push(size);
      if (flags.isOwner) {
        ownerCount += 1;
        ownerSizes.push(size);
      } else if (flags.isMember) {
        memberCount += 1;
        memberSizes.push(size);
      }
    }

    stats.set(userId, {
      inPartyCount: parties.size,
      ownerCount,
      memberCount,
      inPartySizes: sortSizes(inPartySizes),
      ownerSizes: sortSizes(ownerSizes),
      memberSizes: sortSizes(memberSizes),
    });
  }
  return stats;
}

function createdAtMillis(value: string): number {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

function compareAnnouncementPartyPicks(
  left: { size: number; createdAt: string; partyId: string },
  right: { size: number; createdAt: string; partyId: string },
): number {
  return (
    left.size - right.size ||
    createdAtMillis(left.createdAt) - createdAtMillis(right.createdAt) ||
    left.partyId.localeCompare(right.partyId)
  );
}

/**
 * Attach at most one party per user for invite merge fields.
 * Matching uses the campaign's relation and size bounds. If several parties
 * still match, pick the smallest, then the oldest, then party id. One email,
 * one invite — never two links.
 */
export function pickAnnouncementPartiesForUsers(
  links: readonly ShowPartyLink[],
  options: {
    partyRelation: AnnouncementPartyRelation;
    partySizeMin?: number | null;
    partySizeMax?: number | null;
  },
): Map<string, AnnouncementPartyContext> {
  const picked = new Map<string, AnnouncementPartyContext>();
  if (options.partyRelation === "none") {
    return picked;
  }

  const { partiesByUser, partySizes } = indexShowPartyLinks(links);

  for (const [userId, parties] of partiesByUser) {
    const candidates: Array<
      AnnouncementPartyContext & { size: number; createdAt: string }
    > = [];

    for (const [partyId, flags] of parties) {
      if (options.partyRelation === "owner" && !flags.isOwner) {
        continue;
      }
      if (
        options.partyRelation === "member" &&
        (flags.isOwner || !flags.isMember)
      ) {
        continue;
      }
      if (!flags.inviteCode) {
        continue;
      }

      const size = partySizes.get(partyId) ?? 0;
      if (
        !matchesAnnouncementPartySize(
          [size],
          options.partySizeMin,
          options.partySizeMax,
        )
      ) {
        continue;
      }

      candidates.push({
        partyId,
        partyName: flags.partyName,
        inviteCode: flags.inviteCode,
        size,
        createdAt: flags.createdAt,
      });
    }

    candidates.sort(compareAnnouncementPartyPicks);
    const match = candidates[0];
    if (match) {
      picked.set(userId, {
        partyId: match.partyId,
        partyName: match.partyName,
        inviteCode: match.inviteCode,
      });
    }
  }

  return picked;
}

export function showPartyStatsFromCounts(
  counts: Map<string, number>,
): Map<string, ShowPartyStats> {
  const stats = new Map<string, ShowPartyStats>();
  for (const [userId, inPartyCount] of counts) {
    stats.set(userId, {
      inPartyCount,
      ownerCount: 0,
      memberCount: 0,
      inPartySizes: [],
      ownerSizes: [],
      memberSizes: [],
    });
  }
  return stats;
}
